import React, { useState, useEffect, useRef, useCallback } from 'react';
import api, { isSilentNetworkError } from '../../services/api';
import AdminTable from '../components/AdminTable';
import AdminModal from '../components/AdminModal';
import styles from './ChapterManagement.module.css';
import {
  PlusIcon,
  FolderIcon,
  FileIcon,
  UploadIcon,
  TrashIcon,
  RefreshIcon
} from '../../components/Icons';

const PAGE_SIZE = 50;

function ChapterManagement() {
  const [chapters, setChapters] = useState([]);
  const [volumes, setVolumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingChapter, setEditingChapter] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    content_trans: '',
    podcast: '',
    volume_id: '',
    order_index: 1
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [selectedTransFile, setSelectedTransFile] = useState(null);
  const [transFileContent, setTransFileContent] = useState('');

  // 分页 & 筛选状态
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filterVolumeId, setFilterVolumeId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');  // 防抖后的值
  const debounceRef = useRef(null);

  // 搜索防抖
  const handleSearchChange = useCallback((val) => {
    setSearchInput(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchText(val);
      setCurrentPage(1);
    }, 400);
  }, []);

  useEffect(() => {
    fetchVolumes();
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  useEffect(() => {
    fetchChapters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterVolumeId, searchText]);

  const fetchChapters = async () => {
    try {
      setLoading(true);
      const params = {
        skip: (currentPage - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        lite: true,
      };
      if (filterVolumeId) params.volume_id = filterVolumeId;
      if (searchText.trim()) params.search = searchText.trim();

      const response = await api.get('/chapters/', { params });
      setChapters(response.data);

      const total = response.headers['x-total-count'];
      if (total !== undefined) setTotalCount(parseInt(total, 10));
    } catch (error) {
      if (isSilentNetworkError(error)) return;
      console.error('获取章节列表失败:', error);
      alert('获取章节列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchVolumes = async () => {
    try {
      const response = await api.get('/volumes/');
      setVolumes(response.data);
    } catch (error) {
      console.error('获取卷列表失败:', error);
    }
  };

  const handleCreate = () => {
    setEditingChapter(null);
    setFormData({
      title: '',
      content: '',
      content_trans: '',  // 重置翻译内容
      podcast: '',
      volume_id: '',
      order_index: 1
    });
    setSelectedFile(null);
    setFileContent('');
    setSelectedTransFile(null);  // 重置翻译文件
    setTransFileContent('');
    setShowModal(true);
  };

  const handleEdit = async (chapter) => {
    try {
      // lite 模式下列表没有完整内容，需要单独加载
      const res = await api.get(`/chapters/${chapter.id}`);
      const full = res.data;
      setEditingChapter(full);
      setFormData({
        title: full.title || '',
        content: full.content || '',
        content_trans: full.content_trans || '',
        podcast: full.podcast || '',
        volume_id: full.volume_id || '',
        order_index: full.order_index || 1
      });
      setSelectedFile(null);
      setFileContent(full.content || '');
      setSelectedTransFile(null);
      setTransFileContent(full.content_trans || '');
      setShowModal(true);
    } catch (error) {
      if (isSilentNetworkError(error)) return;
      console.error('加载章节详情失败:', error);
      alert('加载章节详情失败');
    }
  };

  const handleDelete = async (chapter) => {
    if (window.confirm(`确定要删除章节 "${chapter.title}" 吗？`)) {
      try {
        await api.delete(`/chapters/${chapter.id}`);
        fetchChapters();
        alert('章节删除成功');
      } catch (error) {
        if (isSilentNetworkError(error)) return;
        console.error('删除章节失败:', error);
        alert('删除章节失败');
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
        alert('请选择JSON文件');
        return;
      }
      
      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const content = event.target.result;
          // 验证是否为有效的JSON
          JSON.parse(content);
          setFileContent(content);
          setFormData({ ...formData, content: content });
        } catch (error) {
          alert('JSON文件格式错误，请检查文件内容');
          setSelectedFile(null);
          setFileContent('');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileContent('');
    setFormData({ ...formData, content: '' });
  };

  // 翻译文件处理函数
  const handleTransFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
        alert('请选择JSON文件');
        return;
      }
      
      setSelectedTransFile(file);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const content = event.target.result;
          // 验证是否为有效的JSON
          JSON.parse(content);
          setTransFileContent(content);
          setFormData({ ...formData, content_trans: content });
        } catch (error) {
          alert('JSON文件格式错误，请检查文件内容');
          setSelectedTransFile(null);
          setTransFileContent('');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleRemoveTransFile = () => {
    setSelectedTransFile(null);
    setTransFileContent('');
    setFormData({ ...formData, content_trans: '' });
  };

  const copyTextToClipboard = async (text, successMessage) => {
    if (!text) {
      alert('暂无可复制的内容');
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      alert(successMessage);
    } catch (err) {
      if (isSilentNetworkError(err)) return;
      console.error('复制内容失败:', err);
      alert('复制内容失败，请手动复制');
    }
  };

  const handleCopyChapterContent = () => {
    const contentSource = editingChapter?.content ?? formData.content;
    copyTextToClipboard(contentSource, '章节内容已复制到剪贴板');
  };

  const handleCopyChapterTranslation = () => {
    const contentSource = editingChapter?.content_trans ?? formData.content_trans;
    copyTextToClipboard(contentSource, '翻译内容已复制到剪贴板');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.volume_id) {
      alert('请填写章节标题和选择所属卷');
      return;
    }

    if (!editingChapter && !formData.content.trim()) {
      alert('请上传章节内容JSON文件');
      return;
    }

    try {
      if (editingChapter) {
        await api.put(`/chapters/${editingChapter.id}`, formData);
        alert('章节更新成功');
      } else {
        await api.post('/chapters/', formData);
        alert('章节创建成功');
      }
      
      setShowModal(false);
      fetchChapters();
    } catch (error) {
      if (isSilentNetworkError(error)) return;
      console.error('保存章节失败:', error);
      alert('保存章节失败');
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const columns = [
    { key: 'id', label: 'ID', width: '80px' },
    { key: 'title', label: '章节标题' },
    { 
      key: 'volume', 
      label: '所属卷', 
      render: (volume) => volume?.title || '未知卷'
    },
    { 
      key: 'novel_title', 
      label: '所属小说', 
      render: (_, chapter) => chapter.volume?.novel?.title || '未知小说'
    },
    { key: 'order_index', label: '排序', width: '80px' },
    { 
      key: 'translation_status', 
      label: '翻译', 
      width: '80px',
      render: (_, chapter) => {
        const hasTrans = chapter.content_trans && chapter.content_trans !== '';
        return (
          <span className={`${styles.translationStatus} ${hasTrans ? styles.translated : styles.untranslated}`}>
            {hasTrans ? '✓' : '✗'}
          </span>
        );
      }
    },
    { 
      key: 'ai_notes_status',
      label: '笔记',
      width: '80px',
      render: (_, chapter) => {
        const hasNotes = chapter.ai_notes && chapter.ai_notes !== '';
        return (
          <span className={`${styles.translationStatus} ${hasNotes ? styles.translated : styles.untranslated}`}>
            {hasNotes ? '✓' : '✗'}
          </span>
        );
      }
    },
    { 
      key: 'podcast_status',
      label: '播客',
      width: '80px',
      render: (_, chapter) => {
        const hasPod = chapter.podcast && chapter.podcast !== '';
        return (
          <span className={`${styles.translationStatus} ${hasPod ? styles.translated : styles.untranslated}`}>
            {hasPod ? '✓' : '✗'}
          </span>
        );
      }
    }
  ];

  return (
    <div className={styles.chapterManagement}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>章节管理</h1>
          <p className={styles.subtitle}>共 {totalCount} 个章节 · 第 {currentPage}/{totalPages} 页</p>
        </div>
        <button className={styles.createBtn} onClick={handleCreate}>
          <PlusIcon size={18} />
          添加章节
        </button>
      </div>

      {/* 筛选栏 */}
      <div className={styles.filterBar}>
        <select
          className={styles.filterSelect}
          value={filterVolumeId}
          onChange={(e) => { setFilterVolumeId(e.target.value); setCurrentPage(1); }}
        >
          <option value="">全部卷</option>
          {volumes.map(v => (
            <option key={v.id} value={v.id}>
              {v.novel?.title} - {v.title}
            </option>
          ))}
        </select>
        <input
          className={styles.filterInput}
          type="text"
          placeholder="搜索章节标题..."
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>

      <AdminTable
        data={chapters}
        columns={columns}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyMessage="暂无章节数据"
      />

      {/* 分页控件 */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(1)}
          >
            首页
          </button>
          <button
            className={styles.pageBtn}
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          >
            上一页
          </button>
          <span className={styles.pageInfo}>
            {currentPage} / {totalPages}
          </span>
          <button
            className={styles.pageBtn}
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          >
            下一页
          </button>
          <button
            className={styles.pageBtn}
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(totalPages)}
          >
            末页
          </button>
        </div>
      )}

      {showModal && (
        <AdminModal
          title={editingChapter ? '编辑章节' : '添加章节'}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
          size="large"
        >
          <div className={styles.form}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>章节标题 *</label>
                <input
                  type="text"
                  className={styles.input}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="请输入章节标题"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>排序序号</label>
                <input
                  type="number"
                  className={styles.input}
                  value={formData.order_index}
                  onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 1 })}
                  min="1"
                  placeholder="1"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>所属卷 *</label>
              <select
                className={styles.select}
                value={formData.volume_id}
                onChange={(e) => setFormData({ ...formData, volume_id: e.target.value })}
                required
              >
                <option value="">请选择卷</option>
                {volumes.map(volume => (
                  <option key={volume.id} value={volume.id}>
                    {volume.novel?.title} - {volume.title}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>章节内容</label>
              
              {editingChapter && formData.content && !selectedFile ? (
                <div className={styles.existingContent}>
                  <div className={styles.contentInfo}>
                    <span className={styles.contentLabel}>当前内容:</span>
                    <span className={styles.contentSize}>
                      {formData.content.length} 字符
                    </span>
                  </div>
                  <div className={styles.contentPreview}>
                    {formData.content.substring(0, 200)}
                    {formData.content.length > 200 && '...'}
                  </div>
                  <div className={styles.existingActions}>
                    <button 
                      type="button" 
                      className={styles.replaceBtn}
                      onClick={() => {
                        const fileInput = document.getElementById('chapter-file-input');
                        if (fileInput) {
                          fileInput.click();
                        }
                      }}
                    >
                      <FolderIcon size={18} />
                      替换内容文件
                    </button>
                    <button
                      type="button"
                      className={styles.copyBtn}
                      onClick={handleCopyChapterContent}
                    >
                      复制内容
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles.fileUpload}>
                  {selectedFile ? (
                    <div className={styles.fileSelected}>
                      <div className={styles.fileInfo}>
                        <span className={styles.fileName}>
                          <FileIcon size={16} />
                          {selectedFile.name}
                        </span>
                        <span className={styles.fileSize}>
                          {fileContent.length} 字符
                        </span>
                      </div>
                      <div className={styles.filePreview}>
                        {fileContent.substring(0, 200)}
                        {fileContent.length > 200 && '...'}
                      </div>
                      <div className={styles.fileActions}>
                        <button 
                          type="button" 
                          className={styles.removeFileBtn}
                          onClick={handleRemoveFile}
                        >
                          <TrashIcon size={16} />
                          移除文件
                        </button>
                        <button 
                          type="button" 
                          className={styles.replaceFileBtn}
                          onClick={() => {
                            const fileInput = document.getElementById('chapter-file-input');
                            if (fileInput) {
                              fileInput.click();
                            }
                          }}
                        >
                          <RefreshIcon size={16} />
                          重新选择
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.uploadArea}>
                      <div className={styles.uploadIcon}>
                        <UploadIcon size={32} />
                      </div>
                      <div className={styles.uploadText}>
                        <p>点击选择JSON文件或拖拽文件到此处</p>
                        <p className={styles.uploadHint}>支持 .json 格式文件</p>
                      </div>
                      <button 
                        type="button" 
                        className={styles.uploadBtn}
                        onClick={() => {
                          const fileInput = document.getElementById('chapter-file-input');
                          if (fileInput) {
                            fileInput.click();
                          }
                        }}
                      >
                        <UploadIcon size={16} />
                        选择文件
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {/* 文件输入元素放在外面，确保总是存在 */}
              <input
                id="chapter-file-input"
                type="file"
                accept=".json,application/json"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>

            {/* 翻译内容上传区域 */}
            <div className={styles.formGroup}>
              <label className={styles.label}>翻译内容 (可选)</label>
              
              {editingChapter && formData.content_trans && !selectedTransFile ? (
                <div className={styles.existingContent}>
                  <div className={styles.contentInfo}>
                    <span className={styles.contentLabel}>当前翻译:</span>
                    <span className={styles.contentSize}>
                      {formData.content_trans.length} 字符
                    </span>
                  </div>
                  <div className={styles.contentPreview}>
                    {formData.content_trans.substring(0, 200)}
                    {formData.content_trans.length > 200 && '...'}
                  </div>
                  <div className={styles.existingActions}>
                    <button 
                      type="button" 
                      className={styles.replaceBtn}
                      onClick={() => {
                        const fileInput = document.getElementById('chapter-trans-file-input');
                        if (fileInput) {
                          fileInput.click();
                        }
                      }}
                    >
                      <FolderIcon size={18} />
                      替换翻译文件
                    </button>
                    <button
                      type="button"
                      className={styles.copyBtn}
                      onClick={handleCopyChapterTranslation}
                    >
                      复制翻译
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles.fileUpload}>
                  {selectedTransFile ? (
                    <div className={styles.fileSelected}>
                      <div className={styles.fileInfo}>
                        <span className={styles.fileName}>
                          <FileIcon size={16} />
                          {selectedTransFile.name}
                        </span>
                        <span className={styles.fileSize}>
                          {transFileContent.length} 字符
                        </span>
                      </div>
                      <div className={styles.filePreview}>
                        {transFileContent.substring(0, 200)}
                        {transFileContent.length > 200 && '...'}
                      </div>
                      <div className={styles.fileActions}>
                        <button 
                          type="button" 
                          className={styles.removeFileBtn}
                          onClick={handleRemoveTransFile}
                        >
                          <TrashIcon size={16} />
                          移除文件
                        </button>
                        <button 
                          type="button" 
                          className={styles.replaceFileBtn}
                          onClick={() => {
                            const fileInput = document.getElementById('chapter-trans-file-input');
                            if (fileInput) {
                              fileInput.click();
                            }
                          }}
                        >
                          <RefreshIcon size={16} />
                          重新选择
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.uploadArea}>
                      <div className={styles.uploadIcon}>
                        <UploadIcon size={32} />
                      </div>
                      <div className={styles.uploadText}>
                        <p>点击选择翻译JSON文件或拖拽文件到此处</p>
                        <p className={styles.uploadHint}>支持 .json 格式文件，可选</p>
                      </div>
                      <button 
                        type="button" 
                        className={styles.uploadBtn}
                        onClick={() => {
                          const fileInput = document.getElementById('chapter-trans-file-input');
                          if (fileInput) {
                            fileInput.click();
                          }
                        }}
                      >
                        <UploadIcon size={16} />
                        选择翻译文件
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {/* 翻译文件输入元素 */}
              <input
                id="chapter-trans-file-input"
                type="file"
                accept=".json,application/json"
                onChange={handleTransFileChange}
                style={{ display: 'none' }}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>播客内容 (可选)</label>
              <textarea
                className={styles.textarea}
                value={formData.podcast}
                onChange={(e) => setFormData({ ...formData, podcast: e.target.value })}
                placeholder="可粘贴播客文本或脚本"
              />
              <p className={styles.inputHint}>可存储播客脚本或音频描述文本</p>
            </div>
          </div>
        </AdminModal>
      )}
    </div>
  );
}

export default ChapterManagement;
